import React, { useState, useRef, useEffect, useCallback } from 'react';
import { apiGet } from '../utils/api';
import { useLatestRequest } from '../hooks/useSearchRequest';
import { formatDateNumeric, formatRelativeAge } from '../utils/dateFormat';
import WarningTriangleIcon from './ui/WarningTriangleIcon';

// Inline SVG icons for user status

const EnvelopeIcon = ({ className }) => (
  <svg className={className || 'w-3.5 h-3.5'} viewBox="0 0 20 20" fill="currentColor">
    <path d="M3 4a2 2 0 00-2 2v1.161l8.441 4.221a1.25 1.25 0 001.118 0L19 7.162V6a2 2 0 00-2-2H3z" />
    <path d="M19 8.839l-7.77 3.885a2.75 2.75 0 01-2.46 0L1 8.839V14a2 2 0 002 2h14a2 2 0 002-2V8.839z" />
  </svg>
);

const ChevronDown = ({ className }) => (
  <svg className={className} viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
    <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.06l3.71-3.83a.75.75 0 111.08 1.04l-4.25 4.39a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z" clipRule="evenodd" />
  </svg>
);

/**
 * Builds "<Verb> on 15/08/26 — 2 days ago" for the placeholder/invited
 * tooltips below, or `null` when there is nothing to append — the caller
 * then falls back to its existing plain-English title, unchanged.
 *
 * `null` (not '') on purpose: `title={x || fallback}` needs a falsy value to
 * fall through, and formatRelativeAge already returns '' rather than '-' for
 * exactly this composability reason (see its own docblock).
 */
function sinceTooltip(verb, sinceDate) {
  if (!sinceDate) return null;
  const dateLabel = formatDateNumeric(sinceDate);
  if (dateLabel === '-') return null; // invalid/unparseable — say nothing rather than "on -"
  const ageLabel = formatRelativeAge(sinceDate);
  return ageLabel ? `${verb} on ${dateLabel} — ${ageLabel}` : `${verb} on ${dateLabel}`;
}

/**
 * Status indicator component — reusable across budget, people manager, etc.
 *
 * `sinceDate` (optional, added 2026-08-17) — an ISO/DATETIME string naming
 * when the CURRENT status began. **Additive and default-off**: omitted, it
 * is `undefined`, `sinceTooltip` returns `null`, and every existing caller
 * (this file's own two internal uses, `GagGroupTable.js`, `PeopleManager.js`,
 * `TeamMemberList.js`) renders byte-identically to before. Only
 * `BudgetCostCodes.js`'s Labour Flow crew table passes it, and only for
 * `placeholder`/`invited` — the owner's ask was scoped to those two states
 * ("show when the invite or place holder was created"); `active`/
 * `deactivated` ignore the prop entirely, unchanged.
 *
 * Kept to ONE `title` per status, same as before the prop existed — never a
 * second overlapping tooltip on a child (the repo's recorded double-tooltip
 * defect).
 */
export function UserStatusIndicator({ status, size = 'sm', sinceDate, hideTitle = false }) {
  const sizeClass = size === 'sm' ? 'w-3.5 h-3.5' : 'w-4 h-4';
  // `hideTitle` (2026-11-03): a parent that carries its OWN tooltip (the
  // Users tab's status pill) passes it, because a child `title` wins over the
  // parent's and would show a second, different text over the icon.
  const tip = (text) => (hideTitle ? undefined : text);
  switch (status) {
    case 'placeholder':
      return <span title={tip(sinceTooltip('Placeholder created', sinceDate) || 'Placeholder — this user has not been fully added to the system yet')}><WarningTriangleIcon className={`${sizeClass} text-yellow-500 dark:text-yellow-400`} /></span>;
    case 'invited':
      return <span title={tip(sinceTooltip('Invite sent', sinceDate) || 'Invited — awaiting user to accept invitation')}><EnvelopeIcon className={`${sizeClass} text-blue-500 dark:text-blue-400`} /></span>;
    case 'active':
      return <span title={tip('Active user')} className={`inline-block ${size === 'sm' ? 'w-2 h-2' : 'w-2.5 h-2.5'} rounded-full bg-green-500`} />;
    case 'deactivated':
      return <span title={tip('Deactivated')} className={`inline-block ${size === 'sm' ? 'w-2 h-2' : 'w-2.5 h-2.5'} rounded-full bg-red-400`} />;
    default:
      return null;
  }
}

/**
 * Combobox for picking an existing user. Looks and behaves like a dropdown:
 * - Chevron icon on the right, rotates when open.
 * - Opens on focus and shows the top 20 users by display_name.
 * - Type to filter (debounced).
 * - If a typed query doesn't match anything, shows an inline "No match —
 *   create them in People first" hint so admins don't sit on a silently-
 *   disabled parent button.
 *
 * Props:
 *  - value: string (display name)
 *  - onChange: (name: string, userId: number|null) => void
 *  - placeholder: string (default: 'Select an existing user')
 *  - className: string (input classes)
 *  - required: boolean
 *  - excludeUserIds: number[] — already-assigned users show disabled in the list
 *  - options (OPTIONAL, 2026-10-02, Gags/teams/tasks Stage 1): a FIXED list of
 *    { id, display_name, status, group? } to pick from — no request is made and
 *    the typed text filters it client-side. Consecutive rows sharing a `group`
 *    get one heading (the task assignee picker lists the gag's TEAM first, then
 *    the rest of the department). Omitted, the server search runs exactly as
 *    before — every existing caller is unchanged.
 *  - clearOnSelect (OPTIONAL): empty the box after a pick (a picker that ADDS
 *    people to a list, rather than one that holds a single value).
 *  - ariaLabel (OPTIONAL): the combobox's accessible name when no <label> exists.
 *  - onCreateNew (OPTIONAL, 2026-11-03): `(typedText) => void`. Adds a last row
 *    "+ Invite “<typed>”…" (or "+ Invite someone new…" when empty) to the
 *    dropdown, and replaces the amber "No match" hint with a quiet row above it.
 *    The one "+ Add person" box (`AddPersonPanel.js`).
 *  - An `options` row may carry `hint` (a short muted label, shown where the
 *    email would be when there is none) — the Add Member picker's shared show.
 */
const DEFAULT_INPUT_CLASS = 'w-full pl-3 pr-9 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 text-sm min-h-[44px] cursor-pointer focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500';

let autocompleteSeq = 0;
function useAutocompleteListboxId() {
  const ref = useRef(null);
  if (ref.current === null) {
    autocompleteSeq += 1;
    ref.current = `user-autocomplete-listbox-${autocompleteSeq}`;
  }
  return ref.current;
}

export default function UserAutocomplete({
  value, onChange, placeholder, className, required, excludeUserIds = [],
  options = null, clearOnSelect = false, ariaLabel,
  onCreateNew = null,
}) {
  const listboxId = useAutocompleteListboxId();
  const [query, setQuery] = useState(value || '');
  const [suggestions, setSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hasFetchedOnce, setHasFetchedOnce] = useState(false);
  const [fetchFailed, setFetchFailed] = useState(false);
  const wrapperRef = useRef(null);
  const debounceRef = useRef(null);

  // Sync external value changes
  useEffect(() => {
    setQuery(value || '');
  }, [value]);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClick(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Stale-response guard. Typing pauses longer than the debounce leave two
  // requests in flight, and a shorter prefix matches MORE rows so it is
  // routinely the SLOWER one — meaning the abandoned query's answer lands last
  // and overwrites the one the user is waiting for. Every setState below is
  // therefore gated on "is my request still the current one".
  // Pinned by UserAutocomplete.staleResponse.test.js.
  const beginRequest = useLatestRequest();

  // `options` mode: a fixed list filtered locally — never a request, so there
  // is nothing to race. Kept OUT of fetchSuggestions below, which is left
  // exactly as it was (it is a declared duplicate of HazardAutocomplete's).
  const filterOptions = useCallback((q) => {
    const term = String(q || '').trim().toLowerCase();
    setSuggestions(options.filter((u) => !term || String(u.display_name || '').toLowerCase().includes(term)));
    setFetchFailed(false);
    setLoading(false);
    setHasFetchedOnce(true);
  }, [options]);

  const fetchSuggestions = useCallback(async (q) => {
    const isCurrent = beginRequest();
    setLoading(true);
    try {
      const url = q
        ? `/api/users/search/autocomplete?q=${encodeURIComponent(q)}`
        : '/api/users/search/autocomplete';
      const res = await apiGet(url);
      // A non-ok response (network reachable, server said no) is exactly as
      // uninformative about "does this person exist" as a rejected fetch — we
      // never got a real answer, so it must not be read as one.
      if (!res.ok) throw new Error(`autocomplete ${res.status}`);
      const data = await res.json();
      if (!isCurrent()) return;
      setSuggestions(data);
      setFetchFailed(false);
    } catch {
      // The request never landed (blocked network, DNS, timeout) or the
      // server refused it — either way we do NOT know whether the person
      // exists, so this must never be read as "no match". See fetchFailed.
      if (!isCurrent()) return;
      setSuggestions([]);
      setFetchFailed(true);
    } finally {
      // Also guarded: a stale response clearing `loading` hides the spinner
      // while the real request is still open, and `showNoMatchHint` reads
      // `!loading && suggestions.length === 0` — so an old empty result would
      // announce "No match" for a name that does exist.
      if (isCurrent()) {
        setLoading(false);
        setHasFetchedOnce(true);
      }
    }
  }, [beginRequest]);
  const loadSuggestions = options ? filterOptions : fetchSuggestions;
  const optionsKey = options ? options.map((o) => o.id).join(',') : null;
  // The latest refilter, read through a ref so the effect keys on the ids alone.
  const refilterRef = useRef(null);
  refilterRef.current = () => {
    if (options && showDropdown && hasFetchedOnce) filterOptions(query);
  };
  useEffect(() => { refilterRef.current(); }, [optionsKey]);

  const handleInputChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    setSelectedUserId(null);
    setSelectedStatus(null);
    onChange(val, null);

    // Debounce API call
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => loadSuggestions(val), 200);
    setShowDropdown(true);
  };

  const handleFocus = () => {
    setShowDropdown(true);
    // If we haven't fetched yet (or the current query is empty), grab the top 20.
    if (!hasFetchedOnce || (!selectedUserId && query.length === 0)) {
      loadSuggestions(query);
    }
  };

  const handleRetry = () => {
    loadSuggestions(query);
  };

  const handleSelect = (user) => {
    setQuery(clearOnSelect ? '' : user.display_name);
    setSelectedUserId(clearOnSelect ? null : user.id);
    setSelectedStatus(clearOnSelect ? null : user.status);
    setSuggestions([]);
    setShowDropdown(false);
    onChange(user.display_name, user.id);
  };

  // "No match" hint — only show when the user typed something >= 2 chars,
  // we've finished fetching, have zero results, AND the fetch that produced
  // that zero actually reached the server. `fetchFailed` is the extra term:
  // without it, a blocked/failed request also reads suggestions.length === 0
  // and this renders "No match — create them in People" for a person we
  // simply never asked about, advising a duplicate.
  // `onCreateNew` (2026-11-03, the one "+ Add person" box): the LAST row of the
  // dropdown is always "+ Invite …", so "nobody matches" is a row above it
  // rather than the amber hint, which then never renders.
  const typed = query.trim();
  const showCreateRow = !!onCreateNew && showDropdown && !loading && !fetchFailed;
  const showNoMatchHint =
    !onCreateNew && showDropdown && !loading && query.length >= 2 && suggestions.length === 0 &&
    hasFetchedOnce && !fetchFailed;
  const handleCreateNew = () => {
    setShowDropdown(false);
    onCreateNew(typed);
  };

  // The request never landed or the server refused it. We do not know
  // whether the person exists, so say that — never "No match" — and offer a
  // retry rather than advice to create anyone.
  const showFetchError = showDropdown && !loading && hasFetchedOnce && fetchFailed;

  return (
    <div ref={wrapperRef} className="relative">
      <input
        type="text"
        role="combobox"
        value={query}
        onChange={handleInputChange}
        onFocus={handleFocus}
        placeholder={placeholder || 'Select an existing user'}
        className={className || DEFAULT_INPUT_CLASS}
        required={required}
        aria-haspopup="listbox"
        aria-expanded={showDropdown}
        aria-autocomplete="list"
        aria-controls={listboxId}
        aria-label={ariaLabel}
      />
      {selectedUserId ? (
        <span className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" title={selectedStatus || 'linked'}>
          <UserStatusIndicator status={selectedStatus || 'active'} />
        </span>
      ) : (
        <span
          className={`absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400 dark:text-gray-500 transition-transform ${showDropdown ? 'rotate-180' : ''}`}
          aria-hidden="true"
        >
          <ChevronDown className="w-5 h-5" />
        </span>
      )}
      {showDropdown && (suggestions.length > 0 || showCreateRow) && (
        <div id={listboxId} role="listbox" className="absolute z-50 mt-1 w-full bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {suggestions.map((user, i) => {
            const isExcluded = excludeUserIds.includes(user.id);
            const heading = user.group && (i === 0 || suggestions[i - 1].group !== user.group) ? (
              <div key={`h-${user.group}`} role="presentation" className="px-3 pt-2 pb-1 text-xs font-semibold text-gray-500 dark:text-gray-400">
                {user.group}
              </div>
            ) : null;
            return [heading, (
              <button
                key={user.id}
                type="button"
                onClick={() => !isExcluded && handleSelect(user)}
                disabled={isExcluded}
                className={`w-full text-left px-3 py-2 min-h-[44px] md:min-h-0 text-sm flex items-center gap-2 ${isExcluded ? 'opacity-50 cursor-not-allowed' : 'hover:bg-indigo-50 dark:hover:bg-gray-600'}`}
              >
                <span className="flex-shrink-0"><UserStatusIndicator status={user.status} /></span>
                <span className="text-gray-800 dark:text-gray-200">{user.display_name}</span>
                {isExcluded && (
                  <span className="text-gray-400 dark:text-gray-500 text-xs italic">(Already assigned)</span>
                )}
                {!isExcluded && user.status === 'placeholder' && (
                  <span className="text-yellow-500 dark:text-yellow-400 text-xs">(placeholder)</span>
                )}
                {user.email && (
                  <span className="text-gray-400 dark:text-gray-500 text-xs ml-auto truncate">{user.email}</span>
                )}
                {!user.email && user.hint && (
                  <span className="text-gray-500 dark:text-gray-400 text-xs ml-auto truncate" data-testid="user-option-hint">{user.hint}</span>
                )}
              </button>
            )];
          })}
          {showCreateRow && suggestions.length === 0 && typed && (
            <div role="presentation" className="px-3 pt-2 pb-1 text-xs text-gray-500 dark:text-gray-400" data-testid="user-autocomplete-no-match">
              <>Nobody you work with matches &ldquo;{typed}&rdquo;.</>
            </div>
          )}
          {showCreateRow && (
            <button
              type="button"
              onClick={handleCreateNew}
              className={`w-full text-left px-3 py-2 min-h-[44px] md:min-h-0 text-sm font-medium text-indigo-700 dark:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-gray-600 ${suggestions.length > 0 ? 'border-t border-gray-200 dark:border-gray-600' : ''}`}
              data-testid="user-autocomplete-create-new"
            >
              {typed ? <>+ Invite &ldquo;{typed}&rdquo;&hellip;</> : '+ Invite someone new…'}
            </button>
          )}
        </div>
      )}
      {showDropdown && loading && (
        <div className="absolute z-50 mt-1 w-full bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg px-3 py-2 text-xs text-gray-400 dark:text-gray-500">
          Searching...
        </div>
      )}
      {showNoMatchHint && (
        <div className="absolute z-50 mt-1 w-full bg-white dark:bg-gray-700 border border-amber-300 dark:border-amber-700 rounded-lg shadow-lg px-3 py-2 text-xs">
          <p className="text-amber-700 dark:text-amber-300 font-medium">No match for &ldquo;{query}&rdquo;</p>
          <p className="text-gray-500 dark:text-gray-400 mt-0.5">
            {'This picker only lists existing users. Create them in the People section first, then they’ll appear here.'}
          </p>
        </div>
      )}
      {showFetchError && (
        <div className="absolute z-50 mt-1 w-full bg-white dark:bg-gray-700 border border-red-300 dark:border-red-700 rounded-lg shadow-lg px-3 py-2 text-xs">
          <p className="text-red-700 dark:text-red-300 font-medium">Couldn&rsquo;t reach the server</p>
          <p className="text-gray-500 dark:text-gray-400 mt-0.5">
            This list may be incomplete. Check your connection and try again.
          </p>
          <button
            type="button"
            onClick={handleRetry}
            className="mt-1 text-indigo-600 dark:text-indigo-400 font-medium hover:underline"
          >
            Retry
          </button>
        </div>
      )}
    </div>
  );
}
